import {Inject, Injectable, Optional, PLATFORM_ID, Renderer2, RendererFactory2} from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import {DOCUMENT, isPlatformBrowser} from "@angular/common";
import {LANG} from "../tokens/lang.token";
import {LanguageService} from "./language.service";

@Injectable({
  providedIn: 'root'
})
export class SEOService {
  public favicon: HTMLLinkElement | null = null;
  private renderer: Renderer2;
  private metas: any;
  constructor(
    private title: Title,
    private meta: Meta,
    private langService: LanguageService,
    private rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private doc: Document,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);

  }

  setMetasRaw(html:string){
    if(this.metas){
      this.renderer.removeChild(this.doc.head.firstChild?.parentNode,this.metas);
    }
    this.metas = this.renderer.createElement("metas");
    this.renderer.appendChild(this.doc.head,this.metas);
    const placeholder = this.renderer.createElement("div");
    placeholder.innerHTML = html;
    if(placeholder.childNodes.length) {
      (Array.from(placeholder.childNodes) as Node[]).map((n:Node)=>{
        this.renderer.appendChild(this.metas,n);
      });
    }
  }

  setRobots(index:string,follow:string){
    this.meta.updateTag({ name: 'robots', content: `${index},${follow}` });
  }

  setOgTitle(title: string){
    this.meta.updateTag({ property: 'og:title', content: title });
  }

  setOgImage(url: string){
    this.meta.updateTag({ property: 'og:image', content: url });
  }
  setOgImageHeight(height: string){
    this.meta.updateTag({ property: 'og:image:height', content: height });
  }
  setOgImageWidth(width: string){
    this.meta.updateTag({ property: 'og:image:width', content: width });
  }
  setOgImageType(type: string){
    this.meta.updateTag({ property: 'og:image:type', content: type });
  }
  setOgDescription(desc: string){
    this.meta.updateTag({ property: 'og:description', content: desc });
  }

  setOgUrl(url: string){
    this.meta.updateTag({ property: 'og:url', content: url });
  }

  setOgType(type: string){
    this.meta.updateTag({ property: 'og:type', content: type });
  }

  setOgLocale(locale: string){
    this.meta.updateTag({ property: 'og:locale', content: locale });
  }

  setOgSiteName(site_name: string){
    this.meta.updateTag({ property: 'og:site_name', content: site_name });
  }

  setTwitterCard(card: string){
    this.meta.updateTag({ name: 'twitter:card', content: card });
  }

  setTwitterSite(site: string){
    this.meta.updateTag({ name: 'twitter:site', content: site });
  }

  setTwitterTitle(title: string){
    this.meta.updateTag({ name: 'twitter:title', content: title });
  }

  setTwitterDescription(description: string){
    this.meta.updateTag({ name: 'twitter:description', content: description });
  }

  setTwitterImage(image: string){
    this.meta.updateTag({ name: 'twitter:image', content: image });
  }

  setTitle(title: string) {
    this.title.setTitle(title);
  }

  setDescription(desc: string) {
    this.meta.updateTag({ name: 'description', content: desc });
  }


  setSchemaJsonLd(schema:any){
      const script = this.renderer.createElement('script');
      script.setAttribute('type', 'application/json');
      script.textContent = JSON.stringify(schema);
      this.renderer.appendChild(this.doc.head,script);
  }

  setCanonical(canonical: string) {
    this.meta.updateTag({ name: 'canonical', content: canonical });
  }

  setFavicon(favicon: string) {
    if (typeof document !== 'undefined') {
      this.favicon = document?.querySelector('#Favicon');
      if (this.favicon) {
        this.favicon.href = favicon;
      }
    }
  }

  setTitles(title:string){
    this.setOgTitle(title);
    this.setTwitterTitle(title);
    this.setTitle(title);
  }

  setDescriptions(desc:string){
    this.setOgDescription(desc);
    this.setTwitterDescription(desc);
    this.setDescription(desc);
  }

  setImages(image:string){
    this.setOgImage(image);
    this.setTwitterImage(image);
  }

  setJsonLdScript(): void {
    const domain = this.langService.current == 'fr' ? "https://www.visitvar.fr" : "https://www.visitvar.com";
    this.renderer.removeChild(this.doc.head, this.doc.querySelector('script[type="application/ld+json"]'));
    const script =  this.renderer.createElement('script');
    script.type = 'application/ld+json';
    script.text = `{
      "@context": "https://schema.org",
      "@type": "WebSite",
      "url": "${domain}",
      "name": "Le Var",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "${domain}?s={query}",
        "query": "required"
      }
    }`;
    this.renderer.appendChild(this.doc.head, script);
  }

  setHrefLangs(slugs: string[], link: string[]) {
    if(this.doc.head){
      const childs = this.doc.querySelector('link[rel="alternate"]');
      if(childs){
        this.renderer.removeChild(this.doc.head, childs);
        childs.remove();
      }
      slugs.forEach((slug, index) => {
        const linkElement = this.renderer.createElement('link');
        linkElement.setAttribute('rel', 'alternate');
        linkElement.setAttribute('hreflang', slug);
        linkElement.setAttribute('href', link[index] ?? '');
        this.renderer.appendChild(this.doc.head, linkElement);
      });
    }
  }

  initHrefLangs(languages:any[],current:string){
    const slugs = [];
    const links = [];
    for(let lang of languages){
      if(lang.slug != current){
        slugs.push(lang.slug);
        links.push(lang.url);
      }
    }
    this.setHrefLangs(slugs, links);
  }
}
